import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useFamily } from '@/contexts/FamilyContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Star,
  User,
  Calendar,
  Phone,
  Droplet,
  ChevronLeft,
  Ruler,
  Weight,
  Loader2
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const relationships = [
  { value: 'self', label: { en: 'Myself', ar: 'أنا' } },
  { value: 'spouse', label: { en: 'Spouse', ar: 'الزوج/الزوجة' } },
  { value: 'child', label: { en: 'Child', ar: 'طفل' } },
  { value: 'parent', label: { en: 'Parent', ar: 'والد/والدة' } },
  { value: 'sibling', label: { en: 'Sibling', ar: 'أخ/أخت' } },
  { value: 'other', label: { en: 'Other', ar: 'آخر' } },
];

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const FamilyMembersPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const { isAuthenticated } = useAuth();
  const { familyMembers, addFamilyMember, updateFamilyMember, removeFamilyMember, setDefaultMember } = useFamily();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    phone: '',
    weight: '',
    height: '',
    alcohol: false,
    smoker: false,
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="dark:bg-slate-800 border-0 shadow-sm max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Users className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {lang === 'ar' ? 'يرجى تسجيل الدخول' : 'Please Login'}
            </h2>
            <p className="text-slate-500 mb-4">
              {lang === 'ar' ? 'تحتاج إلى تسجيل الدخول لإدارة أفراد العائلة' : 'You need to login to manage family members'}
            </p>
            <Button onClick={() => navigate('/login')}>
              {lang === 'ar' ? 'تسجيل الدخول' : 'Login'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleOpenDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        relationship: member.relationship,
        dateOfBirth: member.dateOfBirth,
        gender: member.gender,
        bloodGroup: member.bloodGroup || '',
        phone: member.phone || '',
        weight: member.weight || '',
        height: member.height || '',
        alcohol: member.alcohol || false,
        smoker: member.smoker || false,
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        relationship: '',
        dateOfBirth: '',
        gender: '',
        bloodGroup: '',
        phone: '',
        weight: '',
        height: '',
        alcohol: false,
        smoker: false,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.relationship || !formData.dateOfBirth || !formData.gender) {
      toast.error(lang === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }

    // Ensure name has at least first and last name
    const nameParts = formData.name.trim().split(' ');
    if (nameParts.length < 2 || !nameParts[1]) {
      toast.error(lang === 'ar' ? 'يرجى إدخال الاسم الأول والأخير' : 'Please enter first and last name');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingMember) {
        await updateFamilyMember(editingMember.id, formData);
        toast.success(lang === 'ar' ? 'تم تحديث البيانات' : 'Member updated successfully');
      } else {
        await addFamilyMember(formData);
        toast.success(lang === 'ar' ? 'تمت إضافة فرد العائلة' : 'Family member added successfully');
      }
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Failed to save family member:', error);
      const errorMsg = error.response?.data?.message || error.message;
      toast.error(lang === 'ar' ? 'فشل في حفظ البيانات' : errorMsg || 'Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (member) => {
    if (member.relationship === 'self') {
      toast.error(lang === 'ar' ? 'لا يمكن حذف الملف الشخصي' : 'Cannot delete your own profile');
      return;
    }
    removeFamilyMember(member.id);
    toast.success(lang === 'ar' ? 'تم الحذف' : 'Member removed');
  };

  const handleSetDefault = (member) => {
    setDefaultMember(member.id);
    toast.success(lang === 'ar' ? 'تم تعيينه كافتراضي' : 'Set as default for bookings');
  };

  const getRelationshipLabel = (value) => {
    const rel = relationships.find(r => r.value === value);
    return rel ? (lang === 'ar' ? rel.label.ar : rel.label.en) : value;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="family-members-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white mb-4 hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {lang === 'ar' ? 'رجوع' : 'Back'}
          </Button>
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8" />
            <div>
              <h1 className="text-2xl font-bold">
                {lang === 'ar' ? 'أفراد العائلة' : 'Family Members'}
              </h1>
              <p className="text-teal-100">
                {lang === 'ar' ? 'إدارة الملفات الشخصية للحجز لأفراد الأسرة' : 'Manage booking profiles for family members'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Add Member Button */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="w-full mb-6 bg-teal-600 hover:bg-teal-700" 
              size="lg"
              onClick={() => handleOpenDialog()}
              data-testid="add-family-member-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              {lang === 'ar' ? 'إضافة فرد من العائلة' : 'Add Family Member'}
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingMember 
                  ? (lang === 'ar' ? 'تعديل البيانات' : 'Edit Member')
                  : (lang === 'ar' ? 'إضافة فرد من العائلة' : 'Add Family Member')
                }
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>{lang === 'ar' ? 'الاسم الكامل' : 'Full Name'} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={lang === 'ar' ? 'أدخل الاسم' : 'Enter name'}
                  data-testid="family-member-name"
                />
              </div>

              <div>
                <Label>{lang === 'ar' ? 'العلاقة' : 'Relationship'} *</Label>
                <Select
                  value={formData.relationship}
                  onValueChange={(value) => setFormData({ ...formData, relationship: value })}
                >
                  <SelectTrigger data-testid="family-member-relationship">
                    <SelectValue placeholder={lang === 'ar' ? 'اختر العلاقة' : 'Select relationship'} />
                  </SelectTrigger>
                  <SelectContent>
                    {relationships.map(rel => (
                      <SelectItem key={rel.value} value={rel.value}>
                        {lang === 'ar' ? rel.label.ar : rel.label.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{lang === 'ar' ? 'تاريخ الميلاد' : 'Date of Birth'} *</Label>
                  <Input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    data-testid="family-member-dob"
                  />
                </div>
                <div>
                  <Label>{lang === 'ar' ? 'الجنس' : 'Gender'} *</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) => setFormData({ ...formData, gender: value })}
                  >
                    <SelectTrigger data-testid="family-member-gender">
                      <SelectValue placeholder={lang === 'ar' ? 'اختر' : 'Select'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{lang === 'ar' ? 'ذكر' : 'Male'}</SelectItem>
                      <SelectItem value="female">{lang === 'ar' ? 'أنثى' : 'Female'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{lang === 'ar' ? 'فصيلة الدم' : 'Blood Group'}</Label>
                  <Select
                    value={formData.bloodGroup}
                    onValueChange={(value) => setFormData({ ...formData, bloodGroup: value })}
                  >
                    <SelectTrigger data-testid="family-member-blood">
                      <SelectValue placeholder={lang === 'ar' ? 'اختر' : 'Select'} />
                    </SelectTrigger>
                    <SelectContent>
                      {bloodGroups.map(bg => (
                        <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{lang === 'ar' ? 'رقم الهاتف' : 'Phone'}</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+963..."
                    data-testid="family-member-phone"
                  />
                </div>
              </div>

              {/* Weight and Height */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{lang === 'ar' ? 'الوزن (كجم)' : 'Weight (kg)'}</Label>
                  <Input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    placeholder="70"
                    data-testid="family-member-weight"
                  />
                </div>
                <div>
                  <Label>{lang === 'ar' ? 'الطول (سم)' : 'Height (cm)'}</Label>
                  <Input
                    type="number"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    placeholder="170"
                    data-testid="family-member-height"
                  />
                </div>
              </div>

              {/* Lifestyle - Alcohol and Smoker */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <Checkbox
                    id="smoker"
                    checked={formData.smoker}
                    onCheckedChange={(checked) => setFormData({ ...formData, smoker: !!checked })}
                    data-testid="family-member-smoker"
                  />
                  <Label htmlFor="smoker" className="cursor-pointer">
                    {lang === 'ar' ? 'مدخن' : 'Smoker'}
                  </Label>
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <Checkbox
                    id="alcohol"
                    checked={formData.alcohol}
                    onCheckedChange={(checked) => setFormData({ ...formData, alcohol: !!checked })}
                    data-testid="family-member-alcohol"
                  />
                  <Label htmlFor="alcohol" className="cursor-pointer">
                    {lang === 'ar' ? 'يتناول الكحول' : 'Drinks Alcohol'}
                  </Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
                {lang === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="bg-teal-600 hover:bg-teal-700" 
                data-testid="save-family-member"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{lang === 'ar' ? 'جاري الحفظ...' : 'Saving...'}</>
                ) : editingMember 
                  ? (lang === 'ar' ? 'تحديث' : 'Update')
                  : (lang === 'ar' ? 'إضافة' : 'Add')
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Family Members List */}
        <div className="space-y-4">
          {familyMembers.map((member) => (
            <Card key={member.id} className="dark:bg-slate-800 border-0 shadow-sm" data-testid={`family-member-${member.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                      <User className="h-6 w-6 text-teal-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{member.name}</h3>
                        {member.isDefault && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Star className="h-3 w-3 mr-1" />
                            {lang === 'ar' ? 'افتراضي' : 'Default'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-teal-600">{getRelationshipLabel(member.relationship)}</p>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500 dark:text-slate-400">
                        {member.dateOfBirth && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {member.dateOfBirth}
                          </span>
                        )}
                        {member.bloodGroup && (
                          <span className="flex items-center gap-1">
                            <Droplet className="h-3 w-3" />
                            {member.bloodGroup}
                          </span>
                        )}
                        {member.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {member.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!member.isDefault && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        onClick={() => handleSetDefault(member)}
                        title={lang === 'ar' ? 'تعيين كافتراضي' : 'Set as default'}
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-600 hover:text-teal-600 hover:bg-teal-50"
                      onClick={() => handleOpenDialog(member)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    {member.relationship !== 'self' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-600 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(member)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm mt-6 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-700">
              {lang === 'ar' 
                ? '💡 يمكنك حجز مواعيد لأفراد عائلتك. اختر الفرد المناسب عند الحجز.'
                : '💡 You can book appointments for your family members. Select the appropriate member when booking.'
              }
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FamilyMembersPage;
